import { React, useState, useEffect, useRef } from "react";
import {
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  Typography,
  Button,
  FormHelperText,
  Paper,
  CircularProgress,
} from "@mui/material";
import { Box } from "@mui/system";
import { useMutation } from "@apollo/client";
import { useNavigate } from "react-router-dom";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import VerificationLayout from "../../layout/VerificationLayout.component";
import AppButton from "../../base/AppButton.component";
import InformationDialog from "../../base/dialog-body/Information.dialog";
import { useVerification } from "../../../context/Verification.context";
import { CONSENT_CONNECT, FINALIZE_CANOPY_VERIFICATION } from "../../../graphql/mutation/mutation";
import { teamID, TYPE } from "../../../utils/Utils";
import HocPopup from "../../../hoc/hocPopup";
import { InputValidation } from "../../../utils/InputValidationRegex";
import { useTheme } from "@emotion/react";
import { navigateContinueDelivery, getContinueDeliveryNavTarget } from "../../../utils/videoPlayer";



const InsuranceProviderComponent = ({ dialog, canopyPollStatus }) => {
  const navigate = useNavigate();
  const verification = useVerification();
  const theme = useTheme();

  const [accountCredential, setCredential] = useState({
    username: "",
    password: "",
    thirdField: "",
    showPassword: true,
  });

  const [pendingAuth, setPendingAuth] = useState(null);
  const finalizeCalledRef = useRef(false);
  const [disableButton, setDisableButton] = useState(false);

  const [request, { loading, data }] = useMutation(CONSENT_CONNECT, {
    onError: (err) => {
      console.log(err);
      setDisableButton(false);
    },
  });

  const [finalizeCanopyVerification] = useMutation(FINALIZE_CANOPY_VERIFICATION);

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const handleClickShowPassword = () => {
    setCredential({
      ...accountCredential,
      showPassword: !accountCredential.showPassword,
    });
  };

  useEffect(() => {
    if (!pendingAuth) {
      setDisableButton(false);
    }
  }, [pendingAuth]);

  useEffect(() => {
    if (!data) return;

    if (data.consentAndConnect?.errors?.length) {
      setDisableButton(false);
      dialog({
        open: true,
        bodyType: TYPE.DEFAULT,
        title: "Login failed",
        body: (
          <InformationDialog text={data.consentAndConnect.errors[0].message} />
        ),
      });
      return;
    }

    if (!data.consentAndConnect?.pull_id) {
      setDisableButton(false);
      dialog({
        open: true,
        bodyType: TYPE.DEFAULT,
        title: "Login failed",
        body: (
          <InformationDialog text="Incorrect username or password. Please try again." />
        ),
      });
      return;
    }

    const insuranceInput = {
      employee_user_id: parseInt(verification.employeeId),
      device_identifier: "davi",
      terms_version: 1,
      consent_language:
        "By clicking <name of button> you agree to the Canopy Connect End User Privacy Policy and Terms of Service",
      public_alias: teamID,
      insurer_name: `${verification.insuranceObject?.carrier_id}`,
      insurer_username: accountCredential.username,
      insurer_password: accountCredential.password,
      insurer_thirdField: accountCredential.thirdField,
      transaction_id: verification?.customer?.transactionId,
      customer: { user_id: parseInt(verification?.customer?.userId) },
      dealership_id: parseInt(verification?.dealershipId),
      pull_id: data.consentAndConnect.pull_id,
      pull_jwt: data.consentAndConnect.pull_jwt,
    };
    verification.setLastConsentInsuranceInput?.(insuranceInput);
    verification.setPullInsurance({
      pullId: data.consentAndConnect.pull_id,
      pullJwt: data.consentAndConnect.pull_jwt,
    });
    setPendingAuth({
      pullId: data.consentAndConnect.pull_id,
      pullJwt: data.consentAndConnect.pull_jwt,
      insuranceInput,
    });
  }, [data]);

  useEffect(() => {
    if (!pendingAuth || !canopyPollStatus) return;

    const status = canopyPollStatus.status;
    const eventType = canopyPollStatus.event_type;

    if (eventType === "AUTH_STATUS" && status === "NOT_AUTHENTICATED") {
      verification.setPullInsurance?.(null);
      setPendingAuth(null);
      finalizeCalledRef.current = false;
      dialog({
        open: true,
        bodyType: TYPE.DEFAULT,
        title: "We couldn't sign you in yet :",
        body: (
          <InformationDialog
            text="Please double-check your Username & Password and try again."
            actions={
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                <Button
                  variant="outlined"
                  sx={{ borderColor: "#9e9e9e", borderWidth: "2px", width: "200px" }}
                  onClick={() => {
                    dialog((prev) => ({ ...prev, open: false }));
                  }}
                >
                  Try Again
                </Button>
              </div>
            }
            helperText="Verifying insurance helps finalize your paperwork faster. You can always provide insurance manually if you prefer."
            additionalActions={
              // Update "Continue Delivery" button in error dialogs
              <Button
                variant="outlined"
                sx={{ borderColor: "#9e9e9e", borderWidth: "2px", width: "200px" }}
                onClick={() => {
                  dialog((prev) => ({ ...prev, open: false }));
                  navigateContinueDelivery(
                    navigate,
                    verification,
                    getContinueDeliveryNavTarget(verification),
                  );
                }}
              >
                Continue Delivery
              </Button>
            }
          />
        ),
      });
      return;
    }

    const successStatuses = [
      "AUTHENTICATED",
      "IDENTITY_VERIFICATION",
      "IDENTITY_VERIFICATION_OPTIONS",
      "COMPLETE",
      "SUCCESS",
    ];
    if (!successStatuses.includes(status)) return;

    if (finalizeCalledRef.current) return;
    finalizeCalledRef.current = true;

    finalizeCanopyVerification({
      variables: {
        finalizeInput: {
          pull_id: pendingAuth.pullId,
          status,
          insuranceInput: pendingAuth.insuranceInput,
        },
      },
    })
      .then(() => {
        setPendingAuth(null);
        finalizeCalledRef.current = false;
        navigate("/verification/verifyInsurance", { replace: true });
      })
      .catch((err) => {
        console.error("finalizeCanopyVerification error:", err);
        finalizeCalledRef.current = false;
        dialog({
          open: true,
          bodyType: TYPE.DEFAULT,
          title: "Error",
          body: (
            <InformationDialog text="Failed to save verification. Please try again." />
          ),
        });
      });
  }, [pendingAuth, canopyPollStatus]);

  const handleLogin = () => {
    request({
      variables: {
        insuranceInput: {
          employee_user_id: parseInt(verification.employeeId),
          device_identifier: "davi",
          terms_version: 1,
          consent_language:
            "By clicking <name of button> you agree to the Canopy Connect End User Privacy Policy and Terms of Service",
          public_alias: teamID,
          insurer_name: `${verification.insuranceObject?.carrier_id}`,
          insurer_username: accountCredential.username,
          insurer_password: accountCredential.password,
          insurer_thirdField: accountCredential.thirdField,
          transaction_id: verification?.customer?.transactionId,
          customer: { user_id: parseInt(verification?.customer?.userId) },
          dealership_id: parseInt(verification?.dealershipId),
        },
      },
    });
  };

  return (
    <VerificationLayout>
      <Box sx={{ display: "flex", flex: 1, flexDirection: "column", justifyContent: "flex-end", alignItems: "center", gap: 1 }}>
        <Box sx={{ borderRadius: "18px", background: "#EBF8F1", fontSize: "2.5rem", padding: "20px", display: "flex", justifyContent: "center", alignItems: "center" }}>
          {verification?.insuranceObject?.name?.match(/\b(\w)/g).join("")}
        </Box>
        <Typography variant="h6" align="center">{verification?.insuranceObject?.name}</Typography>
        <Typography variant="p" align="center">{verification?.insuranceObject?.homepageUrl}</Typography>
      </Box>

      <Box sx={{ width: "100%", display: "flex", flexDirection: "column", alignItems: "center", flex: 1, justifyContent: "center", gap: 2 }}>
        <FormControl sx={{ width: "80%" }} variant="outlined">
          <InputLabel color={InputValidation.ProviderUserNameValidation(accountCredential.username) ? "error" : "primary"}>
            {verification.insuranceObject?.usernameFieldPlaceholder}
          </InputLabel>
          <OutlinedInput
            error={InputValidation.ProviderUserNameValidation(accountCredential.username)}
            label={verification.insuranceObject?.usernameFieldPlaceholder}
            value={accountCredential.username}
            onChange={(event) => setCredential({ ...accountCredential, username: event.target.value })}
            sx={{ borderRadius: 2, border: "none" }}
          />
          <FormHelperText sx={{ color: theme.palette.error.main }}>
            {InputValidation.ProviderUserNameValidation(accountCredential.username)
              ? `${verification.insuranceObject?.usernameFieldPlaceholder} Must be minimum 5 characters`
              : " "}
          </FormHelperText>
        </FormControl>

        {!verification.insuranceObject?.disablePasswordField && (
          <FormControl sx={{ width: "80%" }} variant="outlined">
            <InputLabel color={InputValidation.ProviderUserNameValidation(accountCredential.password) ? "error" : "primary"}>
              Password
            </InputLabel>
            <OutlinedInput
              label="Password"
              error={InputValidation.ProviderUserNameValidation(accountCredential.password)}
              value={accountCredential.password}
              type={accountCredential.showPassword ? "password" : "text"}
              onChange={(event) => setCredential({ ...accountCredential, password: event.target.value })}
              sx={{ borderRadius: 2, border: "none" }}
              endAdornment={
                <InputAdornment position="end">
                  <IconButton onClick={handleClickShowPassword} onMouseDown={handleMouseDownPassword} edge="end">
                    {accountCredential.showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              }
            />
            <FormHelperText sx={{ color: theme.palette.error.main }}>
              {InputValidation.ProviderUserNameValidation(accountCredential.password) ? "Password must be minimum 5 characters" : " "}
            </FormHelperText>
          </FormControl>
        )}

        {verification.insuranceObject?.thirdFieldPlaceholder && (
          <FormControl sx={{ width: "80%" }} variant="outlined">
            <InputLabel>{verification.insuranceObject?.thirdFieldPlaceholder}</InputLabel>
            <OutlinedInput
              label={verification.insuranceObject?.thirdFieldPlaceholder}
              value={accountCredential.thirdField}
              onChange={(event) => setCredential({ ...accountCredential, thirdField: event.target.value })}
              sx={{ borderRadius: 2, border: "none" }}
            />
          </FormControl>
        )}
      </Box>

      <Box sx={{ display: "flex", flex: 1, flexDirection: "column", alignItems: "center", justifyContent: "center", width: "100%", gap: 2 }}>
        <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "flex-start", alignItems: "flex-start", width: "80%", gap: 1 }}>
          <Box sx={{ display: "flex", flexDirection: window?.innerWidth <= 750 ? "column" : "row", justifyContent: "flex-start", alignItems: window?.innerWidth <= 750 ? "flex-start" : "center", gap: window?.innerWidth <= 750 ? 0 : 1 }}>
            <Typography variant="caption" align="left">Forget your password ?</Typography>
            <Typography variant="caption" sx={{ cursor: "pointer" }} align="left" color={"primary"} onClick={() => window.open(`${verification.insuranceObject?.resetPasswordUrl}`, "_blank")}>
              Click here to get new one
            </Typography>
          </Box>
          <Box sx={{ display: "flex", flexDirection: window?.innerWidth <= 750 ? "column" : "row", justifyContent: "flex-start", alignItems: window?.innerWidth <= 750 ? "flex-start" : "center", gap: window?.innerWidth <= 750 ? 0 : 1 }}>
            <Typography variant="caption" align="left">Never setup your online account ?</Typography>
            <Typography variant="caption" sx={{ cursor: "pointer" }} align="left" color={"primary"} onClick={() => window.open(`${verification.insuranceObject?.createAccountUrl}`, "_blank")}>
              Click here-It's easy & important
            </Typography>
          </Box>
        </Box>

        {disableButton && (
          <Box sx={{ width: "100%", display: "flex", justifyContent: "center", alignItems: "center", position: "relative", height: 60 }}>
            <CircularProgress color="primary" size={48} thickness={4} />
          </Box>
        )}

        <AppButton
          buttonText={pendingAuth ? "Verifying…" : "Login"}
          loading={loading || !!pendingAuth}
          sx={{ width: "80%", display: disableButton ? "none" : "block" }}
          clickHandler={(event) => {
            event.stopPropagation();
            event.preventDefault();
            if (loading || disableButton) return;
            setDisableButton(true);
            handleLogin();
          }}
          disabled={
            disableButton ||
            !!pendingAuth ||
            accountCredential.username === "" ||
            (!verification.insuranceObject?.disablePasswordField && accountCredential.password === "") ||
            InputValidation.ProviderUserNameValidation(accountCredential.username) ||
            (!verification.insuranceObject?.disablePasswordField && InputValidation.ProviderUserNameValidation(accountCredential.password)) ||
            (verification.insuranceObject?.thirdFieldPlaceholder && accountCredential.thirdField === "")
          }
        />
      </Box>
    </VerificationLayout>
  );
};
export default HocPopup(InsuranceProviderComponent);